import React, { useContext, useEffect, useState } from "react";
import { Provider as MessageSourceProvider } from "react-message-source";
import { ErrorContext } from "../route/error/provider";
import { LanguageContext } from "./languageContext";
import {
  loadCountries,
  loadFeatureToggles,
  loadIndustries,
  loadMessagesSource,
  loadNogaCodes,
  loadProducts,
} from "./loaders";
import { LoadingContext } from "./loaderContext";
import {
  CountryDto,
  IndustryDto,
  NogaCodeDto,
  Product as IProduct,
  ProductsDto,
} from "../api/commercial/generated-types";
import { ProductFamilyType, ProductRefType } from "./product-context/types";
import { ProductFamily } from "./product-context/product-family";

export interface BootstrapperContextType {
  countries: CountryDto[];
  nogaCodes: NogaCodeDto[];
  industries: IndustryDto[];
  featureToggles: Record<string, boolean>;
  families: ProductFamily[];
  allFamilies: ProductFamily[];
}

interface MasterDataState extends BootstrapperContextType {
  messages: Record<string, string>;
}

export const BootstrapperContext =
  React.createContext<BootstrapperContextType>(null);

export const prepareProductHierarchy = (
  products: ProductsDto,
  productRefFromUrl: ProductRefType,
): ProductFamily[] => {
  const families: ProductFamily[] = [];

  Object.keys(ProductFamilyType).forEach((productFamilyType): void => {
    const allProductsForFamily: IProduct[] = products.productList.filter(
      (product: IProduct) => {
        return product.family === productFamilyType;
      },
    );

    if (allProductsForFamily.length === 0) {
      return;
    }

    const productFromUrl: IProduct = products.productList.filter(
      (product: IProduct) => {
        return product.productRef.value === productRefFromUrl;
      },
    )[0];

    const productInUrlFromSalesLink = productFromUrl?.bySalesLink;
    families.push(
      new ProductFamily(
        productFamilyType as IProduct["family"],
        allProductsForFamily,
        productInUrlFromSalesLink ? productRefFromUrl : null,
        productInUrlFromSalesLink ? productFromUrl?.family : null,
      ),
    );
  });

  if (families.length === 0) {
    throw new Error("Product families hierarchy must not be empty");
  }

  return families;
};

// Separate method for handling all persisted backend products
const prepareAllProductHierarchy = (products: ProductsDto): ProductFamily[] => {
  const families: ProductFamily[] = [];

  Object.keys(ProductFamilyType).forEach((productFamilyType): void => {
    const allProductsForFamily: IProduct[] = products.allProducts.filter(
      (product: IProduct) => {
        return product.family === productFamilyType;
      },
    );

    if (allProductsForFamily.length === 0) {
      return;
    }

    families.push(
      new ProductFamily(
        productFamilyType as IProduct["family"],
        allProductsForFamily,
        null,
        null,
      ),
    );
  });

  if (families.length === 0) {
    throw new Error("Product families hierarchy must not be empty");
  }

  return families;
};

/*
 * Component used to load all context data before rendering the application.
 */
const BootstrapperContextProvider = (props: {
  productRef: ProductRefType;
  children: React.ReactNode;
}): React.JSX.Element => {
  const { setError } = useContext(ErrorContext);
  const { language } = useContext(LanguageContext);

  const [masterData, setMasterData] = useState<MasterDataState>({
    countries: null,
    nogaCodes: null,
    industries: null,
    messages: null,
    featureToggles: {},
    families: null,
    allFamilies: null,
  });

  const { setOverlayActive } = useContext(LoadingContext);

  useEffect(() => {
    setOverlayActive(true);

    Promise.all([
      loadCountries(language),
      loadNogaCodes(language),
      loadIndustries(language),
      loadMessagesSource(language),
      loadFeatureToggles(),
      loadProducts(),
    ])
      .then((result) => {
        setMasterData({
          countries: result[0],
          nogaCodes: result[1],
          industries: result[2],
          messages: result[3],
          featureToggles: result[4],
          families: prepareProductHierarchy(result[5], props.productRef),
          allFamilies: prepareAllProductHierarchy(result[5]),
        });
      })
      .catch((error) => {
        setError(error);
      })
      .finally(() => {
        setOverlayActive(false);
      });
  }, [language, props.productRef, setError, setOverlayActive]);

  const {
    countries,
    nogaCodes,
    industries,
    messages,
    featureToggles,
    families,
    allFamilies,
  } = masterData;

  return (
    <BootstrapperContext.Provider
      value={{
        countries,
        nogaCodes,
        industries,
        featureToggles,
        families,
        allFamilies,
      }}
    >
      {messages && (
        <MessageSourceProvider value={messages}>
          {props.children}
        </MessageSourceProvider>
      )}
    </BootstrapperContext.Provider>
  );
};

export default BootstrapperContextProvider;
